// dependencies
import { z } from "zod";
import { useSelector, useDispatch } from "react-redux";
import React, { useState, useEffect } from "react";
import { getMatchedRouteKeyValue, replace, Link } from "yarrr";
import CreatableSelect from "react-select/creatable";
import { useQueryClient } from "@tanstack/react-query";
import Select from "react-select";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";

// components
import Modal from "../../../../Components/Modal";
import Grid from "../../../../Components/Grid";
import Button from "../../../../Components/Button";
import Spacer from "../../../../Components/Spacer";
import TextInput from "../../../../Components/Inputs/Text";
import Radio from "../../../../Components/Inputs/Radio";
import Textarea from "../../../../Components/Inputs/Textarea";
import { Text } from "../../../../Components/Text";
import useForm from "../../../../Hooks/useForm";
import request from "../../../../Api/request";
import { useFeatureFlag, useFeatureFlags } from "../../../../Api/hooks";
import { getCurrentEnvironment } from "../../../../Selectors/app";

// css
import css from "./CreateFeatureFlag.styl";

const DEFAULT_FORM_VALUES = {
	name: "",
	slug: "",
	type: "boolean",
	description: "",
	tags: [],
	initialValue: "true",
	enumElements: [],
	dependencies: [],
	category: "",
};

// all lower, letters only, spaces replaced with dashes, no special characters
const slugify = (str) => {
	return str
		.toLowerCase()
		.replace(/ /g, "-")
		.replace(/[^\w-]+/g, "");
};

const getFallbackExample = (type) => {
	if (type === "boolean") {
		return "false";
	}

	if (type === "string") {
		return "FALLBACK";
	}

	if (type === "number") {
		return "0";
	}
};

// code snippets
const reactCodeSnippet = (flag, type) => `
import { useFeatureFlag } from "../hooks/useFeatureFlag";

const MyComponent = () => {
	const enabled = useFeatureFlag<${type}>("${flag}", false);

	return (
		<div>
			{enabled ? "Feature Flag is enabled" : "Feature Flag is disabled"}
		</div>
	);
}
`;

const nodeCodeSnippet = (flag, type) => `
import { getFeatureFlag } from "@performyard/dto";

const featureEnabled = await getFeatureFlag<${type}>({
	db, companyID, flag: "${flag}"
});

if (!featureEnabled) {
	throw new Error("Feature Flag is disabled");
}
`;

const pythonCodeSnippet = (flag, type) => `
from dto import feature_flags_dto

feature_enabled = yield feature_flags_dto.get_feature_flag(
	self.db.raw_db, company._id, "${flag}"
)

if not feature_enabled:
    raise tornado.web.HTTPError(403)
`;

const environments = [
	{
		value: "production",
		label: "Production",
	},
	{
		value: "sandbox",
		label: "Sandbox",
	},
	{
		value: "preview",
		label: "Preview",
	},
	{
		value: "staging",
		label: "Staging",
	},
];

const slugPrefixMap = {
	ephemeral: "ff-temp",
	permanent: "ff",
	config: "ff-config",
};

export const CreateFeatureFlag = (props) => {
	const dispatch = useDispatch();
	const queryClient = useQueryClient();
	const environment = useSelector(getCurrentEnvironment);
	const slug = useSelector((state) => getMatchedRouteKeyValue(state, "slug", null));
	const isCreating = slug === "create";

	const [copyEnvironment, setCopyEnvironment] = useState("");
	const [tagsInputValue, setTagsInputValue] = useState("");
	const [elementsInputValue, setElementsInputValue] = useState("");
	const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
	const [isDebugMode, setIsDebugMode] = useState(false);
	const featureFlagRequest = useFeatureFlag(environment.name, slug, {
		enabled: slug === "create" ? false : true,
	});

	const environmentFlagsRequest = useFeatureFlags(environment.name);

	const { state, updateForm, validate, setInitialState } = useForm({
		schema: z.object({
			name: z.string().min(1),
			slug: z
				.string()
				.min(1)
				.regex(/^[a-z-]+$/, "The slug must be all lowercase and contain only letters and dashes.")
				.refine(
					(slug) => {
						if (!slug || !state?.category) return true;
						return slug.startsWith(slugPrefixMap[state.category]);
					},
					{
						message: () =>
							`Slug must start with '${slugPrefixMap[state.category]}' for ${state.category} category`,
					}
				),
			type: z.enum(["boolean", "string", "number"]),
			description: z.string().min(1),
			tags: z.array(z.string()),
			enumElements: z.array(z.string()),
			dependencies: z.array(z.string()),
			initialValue: z.string(),
			category: z
				.string()
				.nullable()
				.refine((val) => val === "ephemeral" || val === "permanent" || val === "config", {
					message: "Please select a category for this feature flag",
				}),
		}),
	});

	useEffect(() => {
		if (isCreating) {
			setInitialState({
				...DEFAULT_FORM_VALUES,
			});
			return;
		}

		if (featureFlagRequest.data) {
			let featureFlag = featureFlagRequest.data?.featureFlag ?? {};
			featureFlag.slug = slug;
			featureFlag.enumElements = [];

			if (featureFlag.type === "json") {
				featureFlag.initialValue = JSON.stringify(featureFlag.initialValue, null, 2);
			} else {
				featureFlag.initialValue = featureFlag.initialValue?.toString();
			}

			if (featureFlag.type === "string") {
				featureFlag.enumElements = featureFlag.options.elements;
			}

			setInitialState({
				...featureFlag,
			});
		}
	});

	if (Object.keys(state).length === 0 || environmentFlagsRequest.isLoading) {
		return <div>Loading...</div>;
	}

	const handleKeyDown = (name, event) => {
		if (name === "elements" && !elementsInputValue) {
			return;
		}

		if (name === "tags" && !tagsInputValue) {
			return;
		}

		switch (event.key) {
			case "Enter":
			case "Tab": {
				if (name === "elements") {
					updateForm({ enumElements: [...state.enumElements, elementsInputValue] });
					setElementsInputValue("");
				}

				if (name === "tags") {
					updateForm({ tags: [...state.tags, tagsInputValue] });
					setTagsInputValue("");
				}

				event.preventDefault();
				break;
			}
		}
	};

	const handleChange = (name, val, event) => {
		if (event?.action === "remove-value") {
			if (name === "elements") {
				const nextState = state.enumElements.filter((element) => element !== event.removedValue.id);
				updateForm({ enumElements: nextState });
			}

			if (name === "tags") {
				const nextState = state.tags.filter((tag) => tag !== event.removedValue.id);
				updateForm({ tags: nextState });
			}
		}
	};

	const flagTypes = [
		{
			id: "boolean",
			label: "Boolean",
		},
		{
			id: "string",
			label: "String",
		},
		{
			id: "number",
			label: "Number",
		},
		// {
		// 	id: "json",
		// 	label: "JSON",
		// },
	];

	const isFormValid = validate();

	// we don't want to stop users from updating valid fields on FFs made before the slug validation was added (june 2025)
	const allowSubmitWithSlugErrors =
		Object.keys(isFormValid.errors).length === 1 && isFormValid?.errors?.slug && !isCreating;

	const handleTypeChange = (val) => {
		updateForm({ type: val });

		if (val === "boolean") {
			updateForm({ initialValue: "true" });
		}

		if (val === "string") {
			updateForm({ initialValue: "" });
		}

		if (val === "number") {
			updateForm({ initialValue: "" });
		}

		if (val === "json") {
			updateForm({ initialValue: "" });
		}
	};

	const handleSubmit = async () => {
		if (isCreating) {
			await request({
				method: "POST",
				url: `/${environment.name}/feature-flags`,
				data: state,
			});

			dispatch({ type: "[100] create info toast", message: "Created Feature Flag", actionMessage: "Dismiss" });
		} else {
			await request({
				method: "PATCH",
				url: `/${environment.name}/feature-flags/${slug}`,
				data: state,
			});

			// invalidate the feature flag cache
			queryClient.invalidateQueries(["feature-flag", environment.name, slug]);
			dispatch({ type: "[100] create info toast", message: "Updated Feature Flag", actionMessage: "Dismiss" });
		}

		dispatch(replace("/feature-flags"));
	};

	const handleDelete = async () => {
		await request({
			method: "DELETE",
			url: `/${environment.name}/feature-flags/${slug}`,
		});

		dispatch({ type: "[100] create info toast", message: "Deleted Feature Flag", actionMessage: "Dismiss" });
		dispatch(replace("/feature-flags"));
	};

	const copyToEnvironment = async () => {
		await request({
			method: "POST",
			url: `/${environment.name}/feature-flags/${slug}/copy`,
			data: {
				environment: copyEnvironment,
			},
		});

		dispatch({ type: "[100] create info toast", message: "Copied Feature Flag", actionMessage: "Dismiss" });
	};

	const updateSlugBasedOnCategory = (categoryValue) => {
		const category = categoryValue?.category;
		if (!category) return;

		const prefixMap = {
			ephemeral: "ff-temp",
			config: "ff-config",
			permanent: "ff",
		};

		const prefix = prefixMap[category];
		const currentSlug = state.slug;

		// Handling the empty slug case get's rid of the second layer
		if (!currentSlug) {
			updateForm({ slug: `${prefix}-${currentSlug}` });
			return;
		}
		const allPrefixes = Object.values(prefixMap);
		const hasAnyPrefix = allPrefixes.some((p) => currentSlug.startsWith(p));

		if (hasAnyPrefix) {
			// Find the first matching prefix and remove it
			const matchingPrefix = allPrefixes?.find((p) => currentSlug?.startsWith(p));
			const slugWithoutPrefix = matchingPrefix ? currentSlug?.slice(matchingPrefix?.length) : currentSlug;
			const newSlug = `${prefix}-${slugWithoutPrefix?.replace(/^-+/, "")}`;
			updateForm({ slug: newSlug });
		} else {
			updateForm({ slug: `${prefix}-${currentSlug}` });
		}
	};

	return (
		<div>
			{confirmDeleteModalOpen ? (
				<Modal
					content={`Are you sure you want to delete thie feature flag: ${state.name}`}
					buttons={
						<div style={{ margin: "0 16px" }}>
							<Grid columns={["1fr", "1fr"]}>
								<Button color="red" onClick={() => handleDelete()}>
									Yes, Delete
								</Button>
								<Button onClick={() => setConfirmDeleteModalOpen(false)}>Cancel</Button>
							</Grid>
						</div>
					}
					handleClose={() => setConfirmDeleteModalOpen(false)}
				/>
			) : null}

			<div className={`${css.content} ${isCreating ? css.createContent : ""}`}>
				<Grid columns={isCreating ? ["1fr"] : ["1fr", "1fr"]}>
					<div className={css.panel}>
						<div className={css.section}>
							<div className={css.sectionInformation}>
								<div className={css.title}>
									{slug === "create" ? "Create Feature Flag" : "Edit Feature Flag"}
								</div>
								<Grid columns={["600px", "1fr"]}>
									<div>
										<div className={css.description}>
											Feature flags are used to enable or disable features in the application.
											This is useful for testing features before they are released to the public.
										</div>

										<Spacer size={1} />

										<div>
											<input
												id="debug-mode"
												type="checkbox"
												onChange={(e) => setIsDebugMode(e.target.checked)}
											/>
											<Text variant="caption" weight="medium" as="label" htmlFor="debug-mode">
												Enable Debug Mode
											</Text>
										</div>

										{slug !== "create" ? (
											<div className={css.description}>
												You can bulk update company feature flag values{" "}
												<Link href={`/feature-flags/bulk/${slug}`}>here</Link>.
											</div>
										) : null}
									</div>

									<div />
								</Grid>
							</div>
						</div>

						<div className={css.formContainer}>
							<label className={css.field}>
								<div className={css.label}>Name</div>
								{isFormValid.errors.name ? (
									<div className={css.error}>{isFormValid.errors.name.message}</div>
								) : null}
								<TextInput
									value={state.name}
									onChange={(val) => updateForm({ name: val, slug: slugify(val) })}
								/>
							</label>

							{isCreating ? (
								<label className={css.field}>
									<div className={css.label}>Slug</div>
									{isFormValid.errors.slug && (
										<div className={css.error}>
											{typeof isFormValid?.errors?.slug?.message === "function"
												? isFormValid?.errors?.slug?.message()
												: isFormValid?.errors?.slug?.message}
										</div>
									)}
									<TextInput value={state.slug} onChange={(val) => updateForm({ slug: val })} />
								</label>
							) : null}

							<label className={css.field}>
								<div className={css.label}>Description</div>
								{isFormValid.errors.description ? (
									<div className={css.error}>{isFormValid.errors.description.message}</div>
								) : null}
								<Textarea
									value={state.description}
									onChange={(val) => updateForm({ description: val })}
								/>
							</label>

							<label className={css.field}>
								<div className={css.label}>Category</div>
								{isFormValid.errors.category ? (
									<div className={css.error}>{isFormValid.errors.category.message}</div>
								) : null}
								{!isCreating && (
									<div className={css.subtitle}> Flag category cannot be changed after creation.</div>
								)}
								<Radio
									value={state.category}
									disabled={!isCreating}
									options={[
										{
											id: "ephemeral",
											label: "Ephemeral",
											subtitle:
												"Temporary flags intended for short-term use (feature releases, testing)",
										},
										{
											id: "permanent",
											label: "Permanent",
											subtitle: "Long-term flags meant to stay in the codebase indefinitely",
										},
										{
											id: "config",
											label: "Config",
											subtitle: "Configuration values that control system behavior",
										},
									]}
									onChange={(val) => {
										updateForm({ category: val });
										updateSlugBasedOnCategory({ category: val });
									}}
								/>
							</label>

							{isCreating ? (
								<label className={css.field}>
									<div className={css.label}>Type</div>
									{isFormValid.errors.type ? (
										<div className={css.error}>{isFormValid.errors.type.message}</div>
									) : null}
									<Radio value={state.type} options={flagTypes} onChange={handleTypeChange} />
								</label>
							) : null}

							{state.type === "enum" ? (
								<label className={css.field}>
									<div className={css.label}>Enumerated Elements</div>
									{isFormValid.errors.enum ? (
										<div className={css.error}>{isFormValid.errors.tags.message}</div>
									) : null}
									<Select
										components={{ DropdownIndicator: null }}
										isClearable
										isMulti
										menuIsOpen={false}
										onKeyDown={(event) => handleKeyDown("elements", event)}
										placeholder="Type something and press enter..."
										value={state.enumElements.map((element) => ({ id: element, label: element }))}
										inputValue={elementsInputValue}
										onInputChange={(newValue) => setElementsInputValue(newValue)}
										onChange={(val, event) => handleChange("elements", val, event)}
									/>
								</label>
							) : null}

							<label className={css.field}>
								<div className={css.label}>Initial Value</div>
								{isFormValid.errors.initialValue ? (
									<div className={css.error}>{isFormValid.errors.initialValue.message}</div>
								) : null}

								{state.type === "boolean" ? (
									<Radio
										value={state.initialValue}
										options={[
											{ id: "true", label: "Visible" },
											{ id: "false", label: "Hidden" },
										]}
										onChange={(val) => updateForm({ initialValue: val })}
									/>
								) : null}

								{state.type === "number" ? (
									<TextInput
										value={state.initialValue}
										onChange={(val) => updateForm({ initialValue: val })}
									/>
								) : null}

								{state.type === "string" ? (
									<CreatableSelect
										isClearable={true}
										isSearchable={true}
										onChange={(update, action) => {
											if (action.action === "create-option") {
												updateForm({ initialValue: update.value });
											} else if (action.action === "select-option") {
												updateForm({ initialValue: update.id });
											} else if (action.action === "clear") {
												updateForm({ initialValue: "" });
											}
										}}
										defaultValue={{ value: state.initialValue, label: state.initialValue }}
										options={state.enumElements.map((element) => ({ id: element, label: element }))}
									/>
								) : null}

								{state.type === "json" ? (
									<Textarea
										value={state.initialValue}
										onChange={(val) => updateForm({ initialValue: val })}
									/>
								) : null}

								{state.type === "enum" ? (
									<Select
										isClearable={false}
										isSearchable={true}
										onChange={(val) => updateForm({ initialValue: val.id })}
										options={state.enumElements.map((element) => ({ id: element, label: element }))}
									/>
								) : null}
							</label>

							<label className={css.field}>
								<div className={css.label}>Dependencies</div>
								{isFormValid.errors.dependencies ? (
									<div className={css.error}>{isFormValid.errors.dependencies.message}</div>
								) : null}
								<Select
									isMulti={true}
									isClearable={false}
									isSearchable={true}
									onChange={(val) => {
										updateForm({ dependencies: val.map((v) => v.value) });
									}}
									options={(environmentFlagsRequest?.data?.featureFlags ?? []).map((flag) => ({
										value: flag._id,
										label: flag.name,
									}))}
									defaultValue={state.dependencies.map((element) => ({
										value: element,
										label:
											environmentFlagsRequest?.data?.featureFlags?.find(
												(flag) => flag._id === element
											)?.name ?? element,
									}))}
								/>
							</label>

							<label className={css.field}>
								<div className={css.label}>Tags</div>
								{isFormValid.errors.tags ? (
									<div className={css.error}>{isFormValid.errors.tags.message}</div>
								) : null}
								<CreatableSelect
									components={{ DropdownIndicator: null }}
									isClearable
									isMulti
									menuIsOpen={false}
									onKeyDown={(event) => handleKeyDown("tags", event)}
									placeholder="Type something and press enter..."
									value={state.tags.map((tag) => ({ id: tag, label: tag }))}
									inputValue={tagsInputValue}
									onInputChange={(newValue) => setTagsInputValue(newValue)}
									onChange={(val, event) => handleChange("tags", val, event)}
								/>
							</label>

							<label className={css.field}>
								{Object.keys(isFormValid.errors).length > 0 && !allowSubmitWithSlugErrors ? (
									<>
										<div className={css.error}>Please fix the errors above before submitting.</div>
										{isDebugMode ? (
											<>
												{JSON.stringify(isFormValid.errors, null, 2)}
												<Spacer size={1} />
												{JSON.stringify(state, null, 2)}
											</>
										) : null}
									</>
								) : null}

								<Button
									onClick={handleSubmit}
									disabled={!isFormValid.isValid && !allowSubmitWithSlugErrors}
								>
									{slug === "create" ? "Create Flag" : "Update Flag"}
								</Button>
							</label>

							{slug !== "create" ? (
								<>
									<Spacer size={3} />

									<div className={css.dangerZone}>
										<div className={css.title}>Danger Zone</div>

										{featureFlagRequest.data?.featureFlag?.allow_delete ? (
											<>
												<Spacer size={1} />

												<div className={css.dangerZoneContent}>
													<div className={css.dangerZoneDescription}>
														Deleting a feature flag will remove it from the application.
														This will cause any application code that references this
														feature flag to break.
													</div>

													<Spacer size={2} />
													<Button onClick={() => setConfirmDeleteModalOpen(true)} color="red">
														Delete Feature Flag
													</Button>
												</div>
											</>
										) : (
											<div className={css.dangerZoneContent}>
												<div className={css.dangerZoneDescription}>
													This feature flag is protected. Update the{" "}
													<span className={css.emph}>allow_delete</span> property to delete
													this flag.
												</div>
											</div>
										)}
									</div>
								</>
							) : null}
						</div>
					</div>

					{isCreating ? null : (
						<div className={css.panel}>
							<div className={css.sectionInformation}>
								<div className={css.title}>Copy to environment</div>

								<Spacer size={1} />

								<Grid gap={32} columns={["1fr", "100px"]}>
									<Select
										onChange={(val) => setCopyEnvironment(val.value)}
										isMulti={false}
										isClearable={false}
										options={environments.filter((env) => env.value !== environment.name)}
									/>
									<Button onClick={copyToEnvironment}>Copy Flag</Button>
								</Grid>
							</div>

							<Spacer size={3} />

							<div className={css.codeSnippetSection}>
								<div className={css.codeSnippet}>
									<div className={css.codeSnippetTitle}>Node.js</div>
									<Spacer size={1} />
									<div className={css.codeSnippetContent}>
										<SyntaxHighlighter wrapLines={true} language="typescript" style={oneLight}>
											{nodeCodeSnippet(slug, featureFlagRequest.data.featureFlag.type)}
										</SyntaxHighlighter>
									</div>
								</div>

								<div className={css.codeSnippet}>
									<div className={css.codeSnippetTitle}>React</div>
									<Spacer size={1} />
									<div className={css.codeSnippetContent}>
										<SyntaxHighlighter wrapLines={true} language="typescript" style={oneLight}>
											{reactCodeSnippet(slug, featureFlagRequest.data.featureFlag.type)}
										</SyntaxHighlighter>
									</div>
								</div>

								<div className={css.codeSnippet}>
									<div className={css.codeSnippetTitle}>Python</div>
									<Spacer size={1} />
									<div className={css.codeSnippetContent}>
										<SyntaxHighlighter wrapLines={true} language="python" style={oneLight}>
											{pythonCodeSnippet(slug, featureFlagRequest.data.featureFlag.type)}
										</SyntaxHighlighter>
									</div>
								</div>
							</div>
						</div>
					)}
				</Grid>
			</div>
		</div>
	);
};

export default CreateFeatureFlag;
